/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useDrag } from "react-dnd";
import { useFormContext, UseFormWatch } from "react-hook-form";
import styled, { css } from "styled-components";
import {
  Text,
  theme,
  Box,
  SVGIcon,
  Spacer,
  FlexContainer,
} from "@nordcloud/gnui";
import { includes } from "~/tools";
import { ComponentConfig, ComponentGroup } from "../../constants";
import { WorkflowFormData } from "../../types";
import { ComponentIconWrapper } from "../../WorkflowCanvas";

type Props = {
  componentConfig: ComponentConfig;
};

export function DraggableComponent({ componentConfig }: Props) {
  const { watch } = useFormContext<WorkflowFormData>();

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: componentConfig.group,
    item: componentConfig,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }));

  const isUsed = isComponentUsed(componentConfig, watch);

  if (isDragging || isUsed) {
    return (
      <div ref={dragPreview}>
        <ComponentBox isPreview>
          <Spacer height={componentHeight} />
        </ComponentBox>
      </div>
    );
  }

  return (
    <div ref={drag}>
      <ComponentBox leftBorderColor={componentConfig.color}>
        <FlexContainer css={{ height: componentHeight }}>
          <SVGIcon name="dragAndDrop" size="md" />
          <ComponentIconWrapper color={componentConfig.color}>
            <SVGIcon name={componentConfig.iconName} size="smd" color="white" />
          </ComponentIconWrapper>
          <Spacer width={theme.spacing.spacing02} />
          <Text>{componentConfig.title}</Text>
        </FlexContainer>
      </ComponentBox>
    </div>
  );
}

const isComponentUsed = (
  config: ComponentConfig,
  watch: UseFormWatch<WorkflowFormData>
) => {
  const [triggerComponents, selectDataComponent, sendDataComponents] = watch([
    "triggerComponents",
    "selectDataComponent",
    "sendDataComponents",
  ]);

  switch (config.group) {
    case ComponentGroup.TRIGGER:
      return includes(triggerComponents, config.type);
    case ComponentGroup.DATA:
      return selectDataComponent === config.type;
    case ComponentGroup.OUTPUT:
      return includes(sendDataComponents, config.type);
    default:
      return false;
  }
};

const componentHeight = "1.5rem";

type ComponentBoxProps = {
  leftBorderColor?: string;
  isPreview?: boolean;
};

const ComponentBox = styled(Box)<ComponentBoxProps>`
  margin-top: ${theme.spacing.spacing01};
  padding: ${theme.spacing.spacing03};
  padding-left: ${theme.spacing.spacing01};
  transition: none;

  ${handleStylesFromProps}
`;

function handleStylesFromProps({
  isPreview,
  leftBorderColor = "inherit",
}: ComponentBoxProps) {
  if (isPreview) {
    return css`
      border: 1px dashed ${theme.colors.lights[3]};
    `;
  }

  return css`
    background-color: ${theme.color.background.ui03};
    border: ${theme.borders.disabled};
    border-left: ${theme.spacing.spacing02} solid ${leftBorderColor};
    cursor: grab;

    &:hover {
      background-color: ${theme.color.background.ui04};
    }
  `;
}
